import { Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { SelectBrandItem } from '../../../services/dashboard.service';
import { UtilService } from '../../../services/util.service';
import { SelectItem, SelectMode } from '../../../shared/components/select-menu/select-menu.component';
import { WidgetContainerComponent } from '../../my-brand-screen/widget-container/widget-container.component';
import { ContentCategoryDistributionComponent } from '../../my-competition-screen/content-category-distribution/content-category-distribution.component';
import { ContentCategoryDistributionResponse } from '../../my-competition-screen/content-category-distribution/types/types';
import { AdTypeBrand, AdTypeDistribution } from '../types';
import { round } from '../../../utils/numbers';

@Component({
    selector: 'app-ad-type-distribution',
    imports: [WidgetContainerComponent, ContentCategoryDistributionComponent],
    templateUrl: './ad-type-distribution.component.html',
    styleUrl: './ad-type-distribution.component.scss',
})
export class AdTypeDistributionComponent implements OnChanges {
    @Input() data: AdTypeDistribution | undefined;
    @Input() loading = false;
    @Input() myBrand!: SelectBrandItem;
    @Input() myCompetitors!: SelectBrandItem[];
    @Input() brandColorsMap: Record<string, string> = {};

    private utilService = inject(UtilService);
    protected readonly SelectMode = SelectMode;
    protected contentCategoryData: ContentCategoryDistributionResponse | undefined;
    protected platformControl = new FormControl<string>('allPlatforms', { nonNullable: true });
    protected platformOptions: SelectItem<string>[] = [];

    public isAdType = true;
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['data']) {
            this.buildPlatformOptions();
            this.buildContentCategoryData();
        }
    }

    protected onPlatformChange(): void {
        this.buildContentCategoryData();
    }

    private buildPlatformOptions(): void {
        const allPlatforms = new Set(
            (this.data?.brands ?? []).flatMap(b => Object.keys(b.spend ?? {}))
        );
        this.platformOptions = [
            { key: 'allPlatforms', label: 'All Platforms' },
            ...[...allPlatforms].map(key => ({ key, label: this.utilService.formatTitles(key) })),
        ];
    }

    private buildContentCategoryData(): void {
        if (!this.data?.brands?.length) {
            this.contentCategoryData = undefined;
            return;
        }
        const platform = this.platformControl.value;
        const mapped = this.data.brands.map(brand => ({
            brandKey: brand.key,
            shares: this.toShares(brand, platform),
        }));
        const myBrandKey = this.myBrand?.key;
        const myBrandData = mapped.find(b => b.brandKey === myBrandKey) ?? { brandKey: myBrandKey, shares: {} };
        const sortedShares = Object.fromEntries(
            Object.entries(myBrandData.shares).sort(([, a], [, b]) => b - a)
        );
        this.contentCategoryData = {
            myBrand: { ...myBrandData, shares: sortedShares },
            myCompetitors: mapped.filter(b => b.brandKey !== myBrandKey),
        };
    }

    private toShares(brand: AdTypeBrand, platform: string): Record<string, number> {
        const totals: Record<string, number> = {};
        const platformSpends = platform === 'allPlatforms'
            ? Object.values(brand.spend)
            : [brand.spend[platform] ?? {}];

        for (const platformSpend of platformSpends) {
            for (const [adType, spend] of Object.entries(platformSpend)) {
                if (adType === 'total') continue;
                totals[adType] = (totals[adType] ?? 0) + spend;
            }
        }
        const total = Object.values(totals).reduce((sum, v) => sum + v, 0);
        if (total === 0) return {};
        return Object.fromEntries(
            Object.entries(totals).map(([type, spend]): [string, number] => [
                this.utilService.capitalizeFirstLetter(type).replaceAll('_', ' '),
                this.showSpendInPrecentage(spend, total),
            ])
        );
    }
    private showSpendInPrecentage(spend: number, total: number) {
        return Number(round((spend / total) * 100, 1))
    }
}
